import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import {
	selectAccountMembershipId,
	selectActiveCompanyId,
	selectActiveScope,
	selectCompanyId,
	selectCompanyUsers,
	selectUserFullName,
} from '@shared/api/reduxToolkit';
import {
	CompanyIcon,
	CompanyOutlineIcon,
	ProfileIcon,
	ProfileOutlineIcon,
} from '@shared/assets/icons';
import { i18TranslationNS, Translation } from '@shared/config/i18n';
import { PATHS } from '@shared/constants';
import { useActions } from '@shared/lib/hooks';

const MAX_INLINE_COMPANIES = 3;

export const useHeaderNavigationData = ({ closeAll }) => {
	const { t } = useTranslation(i18TranslationNS);
	const navigate = useNavigate();
	const { companyId: routeCompanyId } = useParams();
	const { activatePrivateContext, activateCompanyContext } = useActions();

	const accountMembershipId = useSelector(selectAccountMembershipId);
	const activeScope = useSelector(selectActiveScope);
	const selectedCompanyId = useSelector(selectActiveCompanyId);
	const companyUsers = useSelector(selectCompanyUsers);
	const { fullName } = useSelector(selectUserFullName);
	const userPrimaryCompanyId = useSelector(selectCompanyId);

	const hasCompanies = companyUsers.length > 0;
	const isCompanyMode = activeScope === 'company' && hasCompanies;
	const activeCompanyId = selectedCompanyId || routeCompanyId || companyUsers[0]?.id || null;
	const activeCompanyName = companyUsers.find((c) => c.id === activeCompanyId)?.name || '';

	const title = isCompanyMode
		? activeCompanyName || t(Translation.HEADER_NAV_FALLBACK_COMPANY)
		: fullName || t(Translation.HEADER_NAV_FALLBACK_NAME);
	const subtitle = isCompanyMode
		? t(Translation.HEADER_NAV_COMPANY_MODE_LABEL)
		: t(Translation.HEADER_NAV_PRIVATE_MODE_LABEL);

	const sortedCompanies = useMemo(() => {
		if (!companyUsers.length || !activeCompanyId) return companyUsers;

		return [...companyUsers].sort((a, b) => {
			if (a.id === activeCompanyId) return -1;
			if (b.id === activeCompanyId) return 1;
			return 0;
		});
	}, [activeCompanyId, companyUsers]);

	const profileLabel = t(Translation.HEADER_NAV_PROFILE_LABEL);

	const { activeItem, switchItems } = useMemo(() => {
		const privateItem = {
			id: 'private',
			label: profileLabel,
			isActive: !isCompanyMode,
			icon: !isCompanyMode ? ProfileIcon : ProfileOutlineIcon,
			onClick: () => {
				if (!accountMembershipId) return;
				activatePrivateContext();
				navigate(`/${accountMembershipId}/${PATHS.user.PROFILE}`);
				closeAll();
			},
		};

		const companyItems = sortedCompanies.map((company) => {
			const isActive = isCompanyMode && company.id === activeCompanyId;

			return {
				id: company.id,
				label: company.name,
				isActive,
				icon: isActive ? CompanyIcon : CompanyOutlineIcon,
				onClick: () => {
					if (!accountMembershipId) return;
					activateCompanyContext(company.id);
					navigate(`/${accountMembershipId}/${company.id}/${PATHS.company.COMPANY_PROFILE}`);
					closeAll();
				},
			};
		});

		const allItems = isCompanyMode
			? [...companyItems, privateItem]
			: [privateItem, ...companyItems];
		const currentItem = allItems.find((item) => item.isActive) || allItems[0] || privateItem;
		const availableSwitchItems = allItems.filter((item) => item.id !== currentItem.id);

		return { activeItem: currentItem, switchItems: availableSwitchItems };
	}, [
		accountMembershipId,
		activeCompanyId,
		isCompanyMode,
		profileLabel,
		navigate,
		activatePrivateContext,
		activateCompanyContext,
		sortedCompanies,
		closeAll,
	]);

	const showAllCompaniesButton = companyUsers.length > MAX_INLINE_COMPANIES;

	// Primary company from backend — always shown separately when companies are collapsed.
	// In user scope switchItems = all companies, so find by id.
	// In company scope if primary = activeCompany it won't be in switchItems → null (already shown as active).
	const primaryCompanyItem = useMemo(() => {
		if (!userPrimaryCompanyId) return null;
		return switchItems.find((item) => item.id === userPrimaryCompanyId) ?? null;
	}, [userPrimaryCompanyId, switchItems]);

	const handleCompanySelect = useCallback(
		(companyId) => {
			if (!accountMembershipId) return;
			activateCompanyContext(companyId);
			navigate(`/${accountMembershipId}/${companyId}/${PATHS.company.COMPANY_PROFILE}`);
			closeAll();
		},
		[accountMembershipId, activateCompanyContext, navigate, closeAll],
	);

	return {
		title,
		subtitle,
		activeItem,
		switchItems,
		showAllCompaniesButton,
		primaryCompanyItem,
		companyCount: companyUsers.length,
		sortedCompanies,
		isCompanyMode,
		activeCompanyId,
		handleCompanySelect,
	};
};
